import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, LoginFormValues, RegisterFormValues, AuthResponse } from '@/shared/schemas/auth-schema'
import { USER_ROLES, type UserRole } from '@shared/constants/user-constants'
import type { ApiResponse } from '@/shared/schemas/api-schema'
import { authApi } from '@shared/api/auth'
import { createDomainAuthUtils } from '@shared/lib/auth-utils'
import { withSentryUser } from '@shared/lib/sentry'
import { toast } from 'sonner'

const adminAuthUtils = createDomainAuthUtils('admin')

interface AdminAuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: {
    message: string
    code?: string
    validationErrors?: Record<string, string[]>
  } | null

  login: (data: LoginFormValues) => Promise<void>
  register: (data: RegisterFormValues) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  refreshToken: () => Promise<boolean>
  clearError: () => void
  handleAuthError: () => void
  getDefaultLandingPage: () => string
  logoutAndClearCache: () => Promise<void>
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (data: LoginFormValues) => {
        set({ isLoading: true, error: null })
        try {
          const apiResponse: ApiResponse<AuthResponse> = await authApi.login(data)
          if (apiResponse.statusCode !== 200 || !apiResponse.data) {
            throw new Error(apiResponse.message || 'Login failed')
          }
          const { id, userName, email, token, fullName, tenantName, tenantId, isSuperAdmin, roles } = apiResponse.data

          const role: UserRole = isSuperAdmin
            ? USER_ROLES.SUPERADMIN
            : (roles?.[0] as UserRole) || USER_ROLES.USER
          const user: User = { id, userName, email, fullName, tenantName, tenantId, role }

          adminAuthUtils.setAccessToken(token)
          adminAuthUtils.setUser(user)

          withSentryUser(user.id, user.email, user.userName)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error: any) {
          console.error('Login failed:', error)
          const backendMessage =
            error?.response?.message ||
            error?.response?.data?.message ||
            error?.message
          set({
            isLoading: false,
            error: {
              message: backendMessage || 'Login failed. Please try again.',
              code: error?.response?.errorCode || error?.response?.data?.errorCode,
              validationErrors: error?.response?.validationErrors,
            },
          })
          throw error
        }
      },

      register: async (data: RegisterFormValues) => {
        set({ isLoading: true, error: null })
        try {
          const apiResponse: ApiResponse<AuthResponse> = await authApi.register(data)
          if (apiResponse.statusCode !== 200 || !apiResponse.data) {
            throw new Error(apiResponse.message || 'Registration failed')
          }
          const { id, userName, email, token } = apiResponse.data
          const user: User = { id, userName, email }

          adminAuthUtils.setAccessToken(token)
          adminAuthUtils.setUser(user)

          withSentryUser(user.id, user.email, user.userName)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          console.error('Registration failed:', error)
          set({
            isLoading: false,
            error: {
              message: error instanceof Error ? error.message : 'Registration failed. Please try again.',
            },
          })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          adminAuthUtils.clearAuth()
          withSentryUser()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        } catch (error) {
          console.error('Logout failed:', error)
          adminAuthUtils.clearAuth()
          withSentryUser()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      logoutAndClearCache: async () => {
        set({ isLoading: true })
        try {
          adminAuthUtils.clearAuth()
          withSentryUser()
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })

          if (typeof window !== 'undefined' && (window as any).__QUERY_CLIENT__) {
            try {
              const queryClient = (window as any).__QUERY_CLIENT__
              queryClient.clear()
            } catch (e) {
              console.log('Failed to clear React Query cache:', e)
            }
          }

          try {
            if (typeof window !== 'undefined') {
              const permissionsKeys = Object.keys(localStorage).filter(key => 
                key.includes('permission') || key.includes('role')
              )
              permissionsKeys.forEach(key => localStorage.removeItem(key))
            }
          } catch (e) {
            console.log('Failed to clear permissions cache:', e)
          }

          if (typeof window !== 'undefined') {
            try {
              const keysToKeep = ['admin-auth-storage']
              const allKeys = Object.keys(localStorage)
              allKeys.forEach(key => {
                if (!keysToKeep.includes(key)) {
                  localStorage.removeItem(key)
                }
              })
              sessionStorage.clear()
            } catch (e) {
              console.log('Failed to clear storage:', e)
            }
          }
        } catch (error) {
          console.error('Enhanced logout failed:', error)
          adminAuthUtils.clearAuth()
          withSentryUser()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      refreshUser: async () => {
        if (!adminAuthUtils.isAuthenticated()) {
          set({ user: null, isAuthenticated: false })
          return
        }

        set({ isLoading: true })
        try {
          const apiResponse: ApiResponse<User> = await authApi.getCurrentUser()
          if (apiResponse.statusCode !== 200 || !apiResponse.data) {
            throw new Error(apiResponse.message || 'Failed to fetch user')
          }
          const user = apiResponse.data
          adminAuthUtils.setUser(user)
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          console.error('Failed to refresh user:', error)
          const tokenRefreshed = await get().refreshToken()
          if (!tokenRefreshed) {
            adminAuthUtils.clearAuth()
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: { message: 'Session expired. Please log in again.', code: 'SESSION_EXPIRED' },
            })
          }
        }
      },

      refreshToken: async () => {
        const refreshToken = adminAuthUtils.getRefreshToken()
        if (!refreshToken) return false

        try {
          const apiResponse: ApiResponse<AuthResponse> = await authApi.refreshToken(refreshToken)
          if (apiResponse.statusCode !== 200 || !apiResponse.data) {
            throw new Error(apiResponse.message || 'Token refresh failed')
          }
          const { id, userName, email, token, fullName, tenantName, tenantId, isSuperAdmin, roles } = apiResponse.data

          const role: UserRole = isSuperAdmin
            ? USER_ROLES.SUPERADMIN
            : (roles?.[0] as UserRole) || USER_ROLES.USER
          const user: User = { id, userName, email, fullName, tenantName, tenantId, role }

          adminAuthUtils.setAccessToken(token)
          adminAuthUtils.setUser(user)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
          return true
        } catch (error) {
          console.error('Failed to refresh token:', error)
          return false
        }
      },

      clearError: () => set({ error: null }),

      handleAuthError: () => {
        adminAuthUtils.clearAuth()
        withSentryUser()
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
        
        const path = typeof window !== 'undefined' ? window.location.pathname : ''
        const isAuthRoute = path.startsWith('/auth')
        if (!isAuthRoute) {
          toast.error('Session expired. Please log in again.', {
            duration: 5000,
            action: {
              label: 'Go to Login',
              onClick: () => {
                if (typeof window !== 'undefined') {
                  window.location.href = '/auth/login'
                }
              },
            },
          })
        }
      },

      getDefaultLandingPage: () => {
        const state = get()
        if (!state.user) return '/auth/login'
        
        const { role } = state.user
        
        switch (role) {
          case USER_ROLES.SUPERADMIN:
          case USER_ROLES.ADMIN:
            return '/transactions'
          case USER_ROLES.VIEWER:
            return '/billing'
          case USER_ROLES.USER:
            return '/transactions'
          default:
            return '/transactions'
        }
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    },
  ),
)
